import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Send, Eye, BellRing, Mail, CheckCircle2, XCircle } from "lucide-react";

const KINDS = [
  { kind: "streak", icon: BellRing, title: "Daily streak reminder", when: "Every day · 6pm UK", desc: "Goes to reps who've practised before but not today. Opt-out respected." },
  { kind: "digest", icon: Mail, title: "Manager weekly digest", when: "Mondays · 8am UK", desc: "One per team manager: sessions, avg score, XP, weakest skill and a suggested drill per rep." },
];

function Preview({ data, onClose }) {
  const ref = useRef(null);
  const navigate = useNavigate();
  useEffect(() => { if (data) ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }, [data]);
  if (!data) return null;
  const onClick = (e) => {
    const a = e.target.closest("a");
    if (!a?.href) return;
    e.preventDefault();
    const url = new URL(a.href, window.location.origin);
    if (url.origin === window.location.origin) navigate(url.pathname + url.search);
    else window.open(url.href, "_blank", "noopener");
  };
  return (
    <div ref={ref} className="rounded-2xl border border-slate-200 bg-slate-50 overflow-hidden scroll-mt-4" data-testid="admin-email-preview">
      <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-slate-200 bg-white">
        <div className="text-sm min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Subject</div>
          <div className="font-semibold text-slate-900 truncate" data-testid="admin-email-preview-subject">{data.subject}</div>
        </div>
        <Button size="sm" variant="ghost" onClick={onClose} className="shrink-0" data-testid="admin-email-preview-close">Close</Button>
      </div>
      <div className="max-h-[70vh] overflow-auto" onClick={onClick} data-testid="admin-email-preview-body" dangerouslySetInnerHTML={{ __html: data.html }} />
    </div>
  );
}

export function AdminEmails() {
  const [log, setLog] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/admin/emails").then((r) => setLog(r.data)).catch(() => setLog({ items: [], runs: [] }));
  useEffect(() => { load(); }, []);

  const show = async (kind) => {
    setBusy(`preview-${kind}`);
    try { const r = await api.get(`/admin/emails/preview/${kind}`); setPreview(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't build the preview"); }
    finally { setBusy(""); }
  };
  const sendTest = async (kind) => {
    setBusy(`test-${kind}`);
    try { const r = await api.post(`/admin/emails/test/${kind}`); toast.success(`Test sent to ${r.data.to}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Test send failed"); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-4" data-testid="admin-emails">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {KINDS.map(({ kind, icon: Icon, title, when, desc }) => (
          <div key={kind} className="card-surface rounded-2xl p-5" data-testid={`admin-email-card-${kind}`}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0"><Icon className="w-5 h-5" /></div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-900">{title}</div>
                <div className="text-[11px] font-bold uppercase tracking-widest text-emerald-700 mt-0.5">{when}</div>
                <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">{desc}</p>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button size="sm" variant="outline" onClick={() => show(kind)} disabled={!!busy} className="rounded-full" data-testid={`admin-email-preview-${kind}`}>
                {busy === `preview-${kind}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4 mr-1.5" />} Preview
              </Button>
              <Button size="sm" onClick={() => sendTest(kind)} disabled={!!busy} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`admin-email-test-${kind}`}>
                {busy === `test-${kind}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />} Send me a test
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Preview data={preview} onClose={() => setPreview(null)} />

      <div className="card-surface rounded-2xl p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="font-semibold text-slate-900">Delivery log</h3>
          {log && <div className="text-xs text-slate-500" data-testid="admin-email-stats">{log.eligible_today} reps due a reminder today · {log.teams} teams</div>}
        </div>
        {!log ? <div className="py-8 flex justify-center text-emerald-600"><Loader2 className="w-5 h-5 animate-spin" /></div> : log.items.length === 0 ? (
          <p className="text-sm text-slate-500 mt-3" data-testid="admin-email-log-empty">Nothing sent yet. The schedules fire automatically; use "Send me a test" to see one now.</p>
        ) : (
          <div className="mt-3 divide-y divide-slate-100" data-testid="admin-email-log">
            {log.items.slice(0, 40).map((it, i) => (
              <div key={i} className="flex items-center gap-3 py-2 text-sm">
                {it.status === "sent" ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" /> : <XCircle className="w-4 h-4 text-red-500 shrink-0" />}
                <span className="px-2 py-0.5 rounded-full bg-slate-100 text-[11px] font-semibold text-slate-600 shrink-0">{it.kind}</span>
                <span className="text-slate-900 font-medium truncate">{it.name}</span>
                <span className="text-slate-500 truncate hidden sm:inline">{it.to}</span>
                <span className="ml-auto text-xs text-slate-400 shrink-0">{new Date(it.ts).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                {it.error && <span className="text-xs text-red-600 truncate max-w-[200px]">{it.error}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
